import { useEffect, useState } from 'react';
import { X, Wine, Star, MapPin, Sparkle, ArrowsLeftRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUpsell } from '../../context/UpsellContext';
import { extractProductAttrs, getEffectiveProductPrice } from '../../utils/userProfile';
import { buildUpsellReasonBullets } from '../../utils/upsellReason';

function productImageUrl(product) {
  if (!product?.image) return null;
  return product.image.startsWith('http') ? product.image : `https:${product.image}`;
}

function formatMoney(n, locale) {
  if (n == null || !Number.isFinite(n) || n <= 0) return '—';
  return `${n.toLocaleString(locale, { minimumFractionDigits: 2 })} $`;
}

export const UpsellModal = () => {
  const { activeModalUpsell, dismiss, dismissSilently, acceptReplace, acceptAddBoth } = useUpsell();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState('why');

  useEffect(() => {
    if (activeModalUpsell) setView('why');
  }, [activeModalUpsell]);

  if (!activeModalUpsell) return null;

  const {
    original,
    alternative,
    headline,
    body,
    reasons,
    priceDeltaPct,
    lineQuantity,
  } = activeModalUpsell;
  const store = original?.selectedStore;
  const locale = i18n.language?.startsWith('fr') ? 'fr-CA' : 'en-CA';
  const priceAlt = getEffectiveProductPrice({ ...alternative, selectedStore: store });
  const priceOrig = getEffectiveProductPrice({ ...original, selectedStore: store });
  const pct = Math.max(1, Math.round((priceDeltaPct || 0) * 100));
  const imageUrl = productImageUrl(alternative);
  const origAttrs = extractProductAttrs(original);
  const altAttrs = extractProductAttrs(alternative);
  const bullets = buildUpsellReasonBullets({ reasons, original, alternative });

  const compareRows = [
    { label: t('upsell.compare_name'), current: original?.name || '—', recommended: alternative?.name || '—' },
    {
      label: t('upsell.compare_price'),
      current: formatMoney(priceOrig, locale),
      recommended: formatMoney(priceAlt, locale),
    },
    {
      label: t('upsell.compare_rating'),
      current:
        origAttrs.rating != null ? `${Number(origAttrs.rating).toFixed(1)}/5` : '—',
      recommended:
        altAttrs.rating != null ? `${Number(altAttrs.rating).toFixed(1)}/5` : '—',
    },
    {
      label: t('upsell.compare_country'),
      current: origAttrs.pays_origine || '—',
      recommended: altAttrs.pays_origine || '—',
    },
    {
      label: t('upsell.compare_region'),
      current: origAttrs.region_origine || '—',
      recommended: altAttrs.region_origine || '—',
    },
    {
      label: t('upsell.compare_grape'),
      current: origAttrs.cepage || '—',
      recommended: altAttrs.cepage || '—',
    },
    {
      label: t('upsell.compare_color'),
      current: origAttrs.color || '—',
      recommended: altAttrs.color || '—',
    },
  ];

  const goProduct = () => {
    dismissSilently();
    navigate(`/produit/${alternative.objectID}`);
  };

  return (
    <div className="upsell-modal-overlay" role="presentation" onClick={dismiss}>
      <div
        role="dialog"
        aria-labelledby="upsell-modal-title"
        aria-describedby="upsell-modal-desc"
        className={`upsell-modal${view === 'compare' ? ' upsell-modal--compare' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="upsell-modal__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                background: 'linear-gradient(135deg, #4B164C, #6B2C6B)',
                borderRadius: 12,
                padding: 10,
                display: 'flex',
              }}
            >
              <Wine size={22} color="#fff" weight="fill" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>
                {t('upsell.upgrade_title')}
              </div>
              <h2
                id="upsell-modal-title"
                style={{
                  margin: 0,
                  fontSize: 18,
                  color: '#4B164C',
                  fontWeight: 700,
                }}
              >
                {headline}
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="upsell-modal__close"
            aria-label={t('close')}
            onClick={dismiss}
          >
            <X size={20} color="#4B164C" />
          </button>
        </div>

        <div className="upsell-modal__body">
          {view === 'why' ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#4B164C',
                  letterSpacing: '0.04em',
                }}
              >
                <Sparkle size={16} weight="fill" />
                {t('upsell.why_section_title')}
              </div>
              <p
                id="upsell-modal-desc"
                style={{
                  margin: '0 0 12px',
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: '#333',
                }}
              >
                {body}
              </p>
              {bullets.length > 0 && (
                <ul
                  style={{
                    margin: '0 0 16px',
                    paddingLeft: 18,
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: '#444',
                  }}
                >
                  {bullets.map((b, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>
                      {b}
                    </li>
                  ))}
                </ul>
              )}

              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  background: '#fff',
                  borderRadius: 16,
                  padding: 16,
                  border: '1px solid #eee',
                }}
              >
                <button
                  type="button"
                  onClick={goProduct}
                  style={{
                    width: 96,
                    height: 112,
                    flexShrink: 0,
                    border: 'none',
                    padding: 0,
                    borderRadius: 12,
                    background: '#f8f6fa',
                    cursor: 'pointer',
                    overflow: 'hidden',
                  }}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Wine size={40} color="#4B164C" style={{ marginTop: 24 }} />
                  )}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <button
                    type="button"
                    onClick={goProduct}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: 15,
                      fontWeight: 600,
                      color: '#4B164C',
                      lineHeight: 1.35,
                    }}
                  >
                    {alternative.name}
                  </button>
                  {alternative.attributes?.pays_origine && (
                    <div
                      style={{
                        fontSize: 12,
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        marginTop: 6,
                      }}
                    >
                      <MapPin size={14} />
                      {alternative.attributes.pays_origine}
                      {alternative.attributes.region_origine &&
                        `, ${alternative.attributes.region_origine}`}
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: 12,
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: '#4B164C',
                      }}
                    >
                      {formatMoney(priceAlt, locale)}
                    </div>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#6B2C6B',
                        background: '#f0e8f2',
                        padding: '4px 10px',
                        borderRadius: 999,
                      }}
                    >
                      {t('upsell.price_plus_pct', { pct })}
                    </span>
                  </div>
                  {lineQuantity > 1 && (
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: '#666' }}>
                      {t('upsell.replace_qty_note', { qty: lineQuantity })}
                    </p>
                  )}
                  {alternative.customer_rating != null && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        marginTop: 8,
                        fontSize: 13,
                        color: '#4B164C',
                      }}
                    >
                      <Star size={14} weight="fill" />
                      {alternative.customer_rating}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div style={{ marginTop: 0 }}>
              <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                {t('upsell.compare_intro')}
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 13,
                    background: '#fff',
                    borderRadius: 12,
                    overflow: 'hidden',
                  }}
                >
                  <thead>
                    <tr style={{ background: '#ede6f0' }}>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: '#4B164C' }}>
                        {t('upsell.compare_attribute')}
                      </th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: '#4B164C' }}>
                        {t('upsell.compare_yours')}
                      </th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: '#4B164C' }}>
                        {t('upsell.compare_recommended')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {compareRows.map((row) => (
                      <tr key={row.label} style={{ borderTop: '1px solid #eee' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#555' }}>
                          {row.label}
                        </td>
                        <td style={{ padding: '10px 12px', color: '#333', verticalAlign: 'top' }}>
                          {row.current}
                        </td>
                        <td style={{ padding: '10px 12px', color: '#333', verticalAlign: 'top' }}>
                          {row.recommended}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="upsell-modal__actions">
            <div className="upsell-modal__btn-row">
              {view === 'why' ? (
                <button
                  type="button"
                  className="upsell-modal__btn upsell-modal__btn--secondary upsell-modal__btn--compare"
                  onClick={() => setView('compare')}
                >
                  <ArrowsLeftRight size={18} />
                  {t('upsell.compare_cta')}
                </button>
              ) : (
                <button
                  type="button"
                  className="upsell-modal__btn upsell-modal__btn--secondary upsell-modal__btn--compare"
                  onClick={() => setView('why')}
                >
                  {t('upsell.back_why')}
                </button>
              )}
            </div>
            <button
              type="button"
              className="upsell-modal__btn upsell-modal__btn--primary"
              onClick={acceptReplace}
            >
              {t('upsell.buy_instead')}
            </button>
            <button
              type="button"
              className="upsell-modal__btn upsell-modal__btn--secondary"
              onClick={acceptAddBoth}
            >
              {t('upsell.add_both')}
            </button>
            <button
              type="button"
              className="upsell-modal__btn upsell-modal__btn--ghost"
              onClick={dismiss}
            >
              {t('upsell.keep')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
